import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpenIcon, ZapIcon, LayersIcon, TrophyIcon, MedalIcon, Gamepad2Icon } from 'lucide-react';
import StudentDashboardDailyExercises from './StudentDashboardDailyExercises';
import StudentDashboardFlashAnzan from './StudentDashboardFlashAnzan';
import StudentDashboardFlashCards from './StudentDashboardFlashCards';
import StudentDashboardChallenges from './StudentDashboardChallenges';
import StudentDashboardCompetitions from './StudentDashboardCompetitions';
import StudentDashboardGames from './StudentDashboardGames';

const TAB_ICONS = [BookOpenIcon, ZapIcon, LayersIcon, TrophyIcon, MedalIcon, Gamepad2Icon];

const TAB_CONFIG = [
  { labelKey: 'navigation.dailyExercises', Component: StudentDashboardDailyExercises, dataKey: 'daily' },
  { labelKey: 'navigation.flashAnzan', Component: StudentDashboardFlashAnzan, dataKey: 'flashAnzan' },
  { labelKey: 'navigation.flashCards', Component: StudentDashboardFlashCards, dataKey: 'flashCards' },
  { labelKey: 'navigation.challenges', Component: StudentDashboardChallenges, dataKey: 'challenges' },
  { labelKey: 'navigation.competitions', Component: StudentDashboardCompetitions, dataKey: 'competitions' },
  { labelKey: 'navigation.games', Component: StudentDashboardGames, dataKey: 'games' },
];

/**
 * إحصائيات لوحة الطالب في تبويبات — مساحة أوضح وتركيز على قسم واحد في كل مرة.
 */
const StudentDashboardStatsTabs = ({ stats, loading, isRTL, activeTab, onTabChange }) => {
  const { t } = useTranslation();
  const safeIndex = Math.min(Math.max(0, activeTab), TAB_CONFIG.length - 1);
  const { Component, dataKey } = TAB_CONFIG[safeIndex];
  const data = stats?.[dataKey] ?? null;

  return (
    <section id="student-dashboard-stats" className="scroll-mt-24 space-y-3">
      <nav
        className="flex gap-1.5 overflow-x-auto pb-1 pt-0.5 [-ms-overflow-style:none] [scrollbar-width:none] sm:flex-wrap sm:gap-2 sm:overflow-visible [&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label={t('dashboard.student.statsTabsNav')}
      >
        {TAB_CONFIG.map((tab, index) => {
          const Icon = TAB_ICONS[index];
          const selected = index === safeIndex;
          return (
            <button
              key={tab.labelKey}
              type="button"
              role="tab"
              id={`student-stats-tab-${index}`}
              aria-selected={selected}
              aria-controls="student-stats-tabpanel"
              tabIndex={selected ? 0 : -1}
              onClick={() => onTabChange(index)}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl border-[2px] px-2.5 py-2 text-start text-xs font-bold transition sm:px-3 sm:text-sm ${
                selected
                  ? 'border-sky-400 bg-gradient-to-br from-sky-50 to-white text-sky-950 shadow-[0_4px_0_0_rgba(14,165,233,0.18)] ring-2 ring-sky-200/70'
                  : 'border-slate-200/90 bg-white/90 text-slate-600 hover:border-sky-200 hover:bg-sky-50/50 hover:text-slate-800'
              }`}
            >
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                  selected ? 'bg-sky-500/15 text-sky-700' : 'bg-slate-100/90 text-slate-500'
                }`}
              >
                <Icon className="h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem]" aria-hidden />
              </span>
              <span className="max-w-[9.5rem] leading-tight sm:max-w-none">{t(tab.labelKey)}</span>
            </button>
          );
        })}
      </nav>

      <div
        id="student-stats-tabpanel"
        role="tabpanel"
        aria-labelledby={`student-stats-tab-${safeIndex}`}
        className="min-h-[12rem]"
      >
        <Component data={data} loading={loading} isRTL={isRTL} />
      </div>
    </section>
  );
};

export default StudentDashboardStatsTabs;
